import './doc-tile.css';

import { Badge } from '@kerfjs/ui/badge';
import { ContentItem } from '@kerfjs/ui/content-item';
import { uiColor } from '@kerfjs/ui/css-values';
import { List } from '@kerfjs/ui/list';
import { LoadingSpinner } from '@kerfjs/ui/loading-spinner';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { FileImage, FileSpreadsheet, FileText, TriangleAlert } from 'lucide';

import { looksLikeVendorQuotation } from '../../../../shared/contract/quotation-detect.js';
import { DocQuoteOffer } from '../../../components/doc-quote-offer.js';
import { type IntakeDoc, selectedDocumentIds } from '../../../data/state.js';
import { quotationNeedsReview } from '../../../lib/attachment-processing.js';
import { formatBytes } from '../../../lib/format.js';
import { documentType } from './document-helpers.js';

function docIconFor(document: IntakeDoc): typeof FileText {
  const mime = document.mimeType ?? '';
  const name = document.filename.toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|bmp|svg|tiff?)$/.test(name)) return FileImage;
  if (mime.includes('spreadsheet') || mime.includes('excel') || /\.(xlsx?|csv|tsv)$/.test(name)) return FileSpreadsheet;
  return FileText;
}

export function DocTile({ document, readOnly }: { document: IntakeDoc; readOnly: boolean }): SafeHtml {
  const size = formatBytes(document.sizeBytes);
  const settled =
    document.extractionStatus === undefined ||
    document.extractionStatus === 'done' ||
    document.extractionStatus === 'failed';
  const type = documentType(document);
  const failureLabel =
    document.kind === 'quotation' || document.kind === 'comparison-sheet'
      ? 'Quotation extraction failed'
      : 'Could not read';
  const selected = selectedDocumentIds.value.includes(document.id);
  // A multi-select Kerf ContentItem tile (HS2-J5C8SH, HS2-SBCVFD): a grid row whose
  // gridcell holds the nested "Add as quote" button. Kerf owns the frame, hover,
  // focus and selected paint and arrow-key movement; init.ts handles Enter (open)
  // and Space (select, Shift/Cmd extend) before wireContentItems.
  return (
    <ContentItem
      interactive
      action="document-select"
      itemId={document.id}
      selectionMode="multiple"
      selected={selected}
      frame="framed"
      className="doc-tile"
      title={document.filename}
      rootAttributes={{ 'data-key': document.id, 'data-document-id': document.id }}
    >
      <div class="doc-tile-body">
        <div class="doc-icon">
          <LucideIcon icon={docIconFor(document)} name="" size={30} color={uiColor('text-quiet')} />
          {document.extractionStatus === 'failed' ? (
            <span
              class="doc-status failed"
              title={document.extractionResult?.extractionError ?? failureLabel}
              aria-label={failureLabel}
            >
              <LucideIcon icon={TriangleAlert} name={failureLabel} size="s" color={uiColor('warning-on-quiet')} />
            </span>
          ) : !settled ? (
            <span class="doc-status">
              <LoadingSpinner label="Processing document" size={16} />
            </span>
          ) : null}
        </div>
        <span class="doc-name">
          <Text variant="span" wrap="anywhere">
            {document.filename}
          </Text>
        </span>
        <Text variant="span" tone="quiet" size="compact" className="doc-size">
          {size !== '' ? size : '—'}
        </Text>
        <Badge tone="neutral" shape="rounded" size="compact" className="doc-kind">
          {type.label}
        </Badge>
        {quotationNeedsReview(document) ? (
          <Badge tone="warning" shape="rounded" size="compact">
            No quotations extracted
          </Badge>
        ) : null}
        {looksLikeVendorQuotation(document) && !readOnly ? (
          <List dividerSides="t">
            <DocQuoteOffer docId={document.id} />
          </List>
        ) : null}
      </div>
    </ContentItem>
  );
}
